import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { catchError, of } from 'rxjs';
import { URLService } from './dynamic-url.service';

/** One scheduled task, as pencil-api projects it (SCHEDULED_TASKS_SPEC.md). */
export interface AiScheduleView {
  id: string;
  prompt: string;
  /** e.g. "every weekday at 5:00 pm (America/Los_Angeles)" */
  description: string;
  status: 'active' | 'paused' | string;
  pausedReason?: string;
  ownerUid: string;
  ownerName?: string;
  lastFiredAt?: string;
  lastResult?: string;
  runCount: number;
}

/**
 * The space's scheduled tasks. Created by asking Sparky ("every weekday at 5 pm,
 * summarise…"); here members can see them and pause, resume or delete them.
 * `ai_schedules_changed` realtime events carry the whole list, so every open panel
 * stays in step without polling.
 */
@Injectable({ providedIn: 'root' })
export class AiSchedulesService {
  private readonly subject = new BehaviorSubject<AiScheduleView[]>([]);
  private spaceId: string | null = null;

  constructor(private http: HttpClient, private urlService: URLService) {}

  get schedules$(): Observable<AiScheduleView[]> {
    return this.subject.asObservable();
  }

  async load(spaceId: string): Promise<void> {
    this.spaceId = spaceId;
    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/schedules`;
    const res = await firstValueFrom(
      this.http.get<{ schedules: AiScheduleView[] }>(url).pipe(catchError(() => of(null)))
    );
    if (res && this.spaceId === spaceId) this.subject.next(res.schedules ?? []);
  }

  /** A realtime push for `spaceId` (the whole list). Ignored for another space. */
  apply(spaceId: string, schedules: AiScheduleView[] | undefined): void {
    if (spaceId === this.spaceId && Array.isArray(schedules)) this.subject.next(schedules);
  }

  /** Returns an error message to show, or null on success. */
  async change(spaceId: string, id: string, action: 'pause' | 'resume' | 'delete'): Promise<string | null> {
    const url = `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/schedules/${id}`;
    const req =
      action === 'delete'
        ? this.http.delete<{ schedule: AiScheduleView }>(url)
        : this.http.patch<{ schedule: AiScheduleView }>(url, { action });
    try {
      await firstValueFrom(req);
      await this.load(spaceId);
      return null;
    } catch (e: any) {
      return e?.error?.message || 'Could not change the schedule. Try again.';
    }
  }
}
